import React, { FC, useEffect, useMemo, useState } from 'react'
import { Helmet } from 'react-helmet'
import { useHistory, useLocation, useParams } from 'react-router-dom'
import { getGetPrams, setPath } from '../../../utils/url'
import {
  endOfMonth,
  format, nowDate, startOfMonth,
} from '../../../utils/date'
import { useDispatch, useSelector } from 'react-redux'
import { RootState } from '../../../states/containers/store'
import SearchModal from '../../components/report/detail/SearchModal'
import { fetchKeyword, fetchSearchRequest } from '../../../states/containers/search/listSlice'
import { URL } from '../../constants/url'
import UserData from '../../components/data/UserData'
import { fetchCustomSummary, fetchDailySummary } from '../../../states/containers/dataSummary/summarySlice'
import Category from '../../components/data/Category'
import Application from '../../components/data/Application'
import Wordcloud from '../../components/report/detail/Wordcloud'
import Holidays from 'date-holidays'
import {
  updateCurrentDate,
  updateCurrentEndDate,
  updateCurrentMonth,
  updateCurrentStartDate,
  updateSummaryRangeType,
} from '../../../states/containers/common/currentSlice'
import RangeBlock from '../../components/data/RangeBlock'
import { mapAllowedDate } from '../../../utils/plan'
import LoadingSquare from '../../components/common/LoadingSquare'
import { CustomSummaryResponse, DailySummaryResponse } from '../../../states/types/dataSummary'
import MixChart from '../../components/data/MixChart'
import MemberSummaryModule from '../../components/task/MemberSummaryModule'
import { fetchTaskReportMember } from '../../../states/containers/task/taskSlice'
import PdfModule from '../../components/common/PdfModule'
import Timeline from '../../components/common/Timeline'

interface SearchModal {
  startDate: string
  endDate: string
  teamIds: number[]
  teamNames: string[]
  memberIds: number[]
  memberNames: string[]
  keywords: string[]
  serviceNames: string[]
  categoryIds: number[]
  categoryNames: string[]
  efficiencyLevel: string[]
  stressLevel: string[]
  focusLevel: string[]
  lowerLimitTime?: number
}

type LangValue = 'ja' | 'en'

type DisplayType = "daily" | "weekly" | "monthly" | "custom"
interface GetParams {
  type: string
  date?: string
  target_month?: string
  target_start_date?: string
  target_end_date?: string
}

const Index: FC = () => {
  const location = useLocation()
  const dispatch = useDispatch()
  const { language } = useParams<{ language: LangValue }>()
  const history = useHistory()

  const LOWER_LIMIT_TIME_DEFAULT = 300

  const {
    login,
    commonPart,
    summary,
    current,
    task,
  } = useSelector((state: RootState) => state)

  const holidays = new Holidays()
  holidays.init('JP')

  const searchModalInit = {
    startDate: "",
    endDate: "",
    teamIds: [],
    teamNames: [],
    memberIds: [],
    memberNames: [],
    keywords: [],
    serviceNames: [],
    categoryIds: [],
    categoryNames: [],
    efficiencyLevel: [],
    stressLevel: [],
    focusLevel: [],
    lowerLimitTime: LOWER_LIMIT_TIME_DEFAULT,
  }

  const [searchModal, setSearchModal] = useState<SearchModal>(searchModalInit)

  const [startDate, setStartDate] = useState<string>()
  const [endDate, setEndDate] = useState<string>()

  const [type, setType] = useState<DisplayType>("daily")

  const [isSearchModalDisplay, setIsSearchModalDisplay] = useState<boolean>(false)

  const [data, setData] = useState<DailySummaryResponse | CustomSummaryResponse>()

  const [isDisablePdf, setIsDisablePdf] = useState<boolean>(true)

  const pdfTitle = useMemo(() => {
    let rangeText = ""
    if (type === "daily") {
      rangeText = format(startDate, "YYYY年MM月DD日")
    } else {
      rangeText = `${format(startDate, "YYYY年MM月DD日")} ~ ${format(endDate, "YYYY年MM月DD日")}`
    }
    return `Qaseeセルフマネジメントサマリー ${data?.userdata?.name} ${rangeText}`
  }, [type, data, startDate, endDate])

  const searchModalDetail = (searchDetail: SearchModal) => {
    setIsSearchModalDisplay(true)
    setSearchModal({
      startDate: searchDetail.startDate,
      endDate: searchDetail.endDate,
      teamIds: searchDetail.teamIds,
      teamNames: searchDetail.teamNames,
      memberIds: searchDetail.memberIds,
      memberNames: searchDetail.memberNames,
      keywords: searchDetail.keywords,
      serviceNames: searchDetail.serviceNames,
      categoryIds: searchDetail.categoryIds,
      categoryNames: searchDetail.categoryNames,
      efficiencyLevel: searchDetail.efficiencyLevel,
      stressLevel: searchDetail.stressLevel,
      focusLevel: searchDetail.focusLevel,
      lowerLimitTime: searchDetail.lowerLimitTime || LOWER_LIMIT_TIME_DEFAULT,
    })
    dispatch(fetchSearchRequest(
      {
        start_date: searchDetail.startDate,
        end_date: searchDetail.endDate,
        team_ids: searchDetail.teamIds,
        member_ids: searchDetail.memberIds,
        keywords: searchDetail.keywords,
        service_names: searchDetail.serviceNames,
        category_ids: searchDetail.categoryIds,
        efficiency_level: searchDetail.efficiencyLevel,
        stress_level: searchDetail.stressLevel,
        focus_level: searchDetail.focusLevel,
        lower_limit_time: searchDetail.lowerLimitTime || LOWER_LIMIT_TIME_DEFAULT,
      },
      () => {
        //
      },
    ))
  }

  const handleChangeDate = (value: string) => {
    history.push(`${setPath(URL.DATA_SUMMARY.path, language)}?type=daily&date=${value}`)
  }

  const handleChangeWeek = (value: {startDate: string, endDate: string}) => {
    if (value.startDate !== value.endDate) {
      history.push(`${setPath(URL.DATA_SUMMARY.path, language)}?type=weekly&target_start_date=${value.startDate}&target_end_date=${value.endDate}`)
    }
  }

  const handleChangeMonth = (value: number) => {
    history.push(`${setPath(URL.DATA_SUMMARY.path, language)}?type=monthly&target_month=${value}`)
  }

  const handleChangeRange = (value: {startDate: string, endDate: string}) => {
    if (value.startDate !== value.endDate) {
      history.push(`${setPath(URL.DATA_SUMMARY.path, language)}?type=custom&target_start_date=${value.startDate}&target_end_date=${value.endDate}`)
    }
  }

  useEffect(() => {
    if (summary.isLoading) return
    let memberIds = []
    let searchEndDate = endDate
    if (type === "daily") {
      memberIds = summary.dailySummary?.userdata?.id ? [summary.dailySummary.userdata.id] : []
      searchEndDate = startDate
    } else {
      memberIds = summary.customSummary?.userdata?.id ? [summary.customSummary.userdata.id] : []
    }
    if (memberIds.length) {
      dispatch(fetchKeyword({
        start_date: format(startDate, "YYYY-MM-DD") || nowDate(),
        end_date: format(searchEndDate, "YYYY-MM-DD") || nowDate(),
        member_ids: memberIds,
      }))
      dispatch(fetchTaskReportMember({
        id: memberIds[0].toString() || "0",
        start_date: format(startDate, "YYYY-MM-DD") || nowDate(),
        end_date: format(searchEndDate, "YYYY-MM-DD") || nowDate(),
      }))
    }
  }, [summary.isLoading])

  useEffect(() => {
    window.scrollTo(0, 0)
  }, [location])

  useEffect(() => {
    if (!commonPart.company) return
    const getParams = getGetPrams(location.search) as GetParams
    const summaryType = (getParams.type || current.summaryRangeType || "daily") as DisplayType
    dispatch(updateSummaryRangeType(summaryType))
    setType(summaryType)
    const targetDateParam = getParams.date || current.targetDate
    dispatch(updateCurrentDate(mapAllowedDate(targetDateParam, commonPart.company.plan, commonPart.company.created_at)))
    const targetMonthParam = getParams.target_month || current.targetMonth
    dispatch(updateCurrentMonth(targetMonthParam))
    const targetStartDateParam = getParams.target_start_date || format(current.targetStartDate, 'YYYY-MM-DD')
    dispatch(updateCurrentStartDate(targetStartDateParam))
    const targetEndDateParam = getParams.target_end_date || format(current.targetEndDate, 'YYYY-MM-DD')
    dispatch(updateCurrentEndDate(targetEndDateParam))
    if (summaryType === "daily") {
      setStartDate(mapAllowedDate(targetDateParam, commonPart.company.plan, commonPart.company.created_at))
    } else if (summaryType === "monthly") {
      setStartDate(startOfMonth(`${targetMonthParam}01`))
      setEndDate(endOfMonth(`${targetMonthParam}01`))
    } else {
      setStartDate(targetStartDateParam)
      setEndDate(targetEndDateParam)
    }
  }, [location])

  useEffect(() => {
    if (!login.accessToken) return
    if (!startDate) return
    if (type === "daily") {
      dispatch(fetchDailySummary({ target_date: format(startDate, "YYYY-MM-DD") }))
      dispatch(updateCurrentDate(format(startDate, "YYYY-MM-DD")))
    } else {
      dispatch(fetchCustomSummary({ start_date: format(startDate, "YYYY-MM-DD"), end_date: format(endDate, "YYYY-MM-DD") }))
      dispatch(updateCurrentStartDate(format(startDate, "YYYY-MM-DD")))
      dispatch(updateCurrentEndDate(format(endDate, "YYYY-MM-DD")))
    }
  }, [login.accessToken, type, startDate, endDate])

  useEffect(() => {
    if (summary.isLoading) return
    if (type === "daily") {
      setData(summary.dailySummary)
    } else {
      setData(summary.customSummary)
    }
  }, [summary.isLoading])

  useEffect(() => {
    if (
      summary.isLoading
      || task.isLoading
    ) {
      setIsDisablePdf(true)
      return
    }
    setTimeout(() => {
      setIsDisablePdf(false)
    }, 5000)
  }, [summary, task])

  return (
    <section className="summary_page">
      <Helmet>
        <title>Qasee - セルフマネジメント - サマリー</title>
      </Helmet>
      <section className="breadcrumb">
        セルフマネジメント
        &nbsp;&gt;&nbsp;
        サマリー
        &nbsp;
      </section>
      <section className="page_header">
        <div className="report_title">
          サマリー
        </div>
        <div className="tools">
          <RangeBlock
            changeDateCallback={handleChangeDate}
            changeWeekCallback={handleChangeWeek}
            changeMonthCallback={handleChangeMonth}
            changeRangeCallback={handleChangeRange}
          />
        </div>
      </section>
      <section className="page_header">
        <div />
        <div className="tools">
          <PdfModule
            title={pdfTitle}
            target="pdf_area"
            disabled={isDisablePdf}
          />
        </div>
      </section>
      <section id="pdf_area">
        {
          summary.isLoading ? (
            <LoadingSquare>
              <>
                サマリーを生成しています…
              </>
            </LoadingSquare>
          ) : (
            <>
              {
                data?.userdata ? (
                  <>
                    <section className="summary_wrap">
                      <div className="summary_title no_trans">
                        {
                          data.userdata.profile_image ? (
                            <img alt="profile" src={data.userdata.profile_image} className="profile_icon" />
                          ) : (
                            <img
                              alt=""
                              src={`/static/images/profile/${(data.userdata.id || 0) % 12}.jpeg`}
                              className="profile_icon"
                            />
                          )
                        }
                        {data.userdata.name}
                      </div>
                      <section className="page_contents">
                        <div className="content-block">
                          <div className="block-detail">
                            <UserData
                              isLoading={summary.isLoading}
                              userData={data.userdata || undefined}
                              mode={type}
                            />
                          </div>
                        </div>
                      </section>
                      {
                        (type === "daily" && "timeline" in data) && (
                          <>
                            <section className="page_contents">
                              <div className="content-block">
                                <div className="block-header">
                                  <div className="title">
                                    操作量と作業データの推移
                                  </div>
                                </div>
                                <div className="block-detail">
                                  {
                                    data?.timeline ? (
                                      <Timeline
                                        member={{
                                          ...data.timeline,
                                          id: data.userdata.id,
                                          name: data.userdata.name,
                                          kana: "",
                                          code: "",
                                          profile_image: data.userdata.profile_image,
                                          work_time: 0,
                                          inactive_time: 0,
                                          prev_date: data.timeline.prev_date || "",
                                          teams: [],
                                        }}
                                        targetDate={startDate || nowDate()}
                                        displayHeader={false}
                                        maxValue={data.timeline.max_action_value}
                                      />
                                    ) : (
                                      <>
                                        データがありません
                                      </>
                                    )
                                  }
                                </div>
                              </div>
                            </section>
                          </>
                        )
                      }
                      {
                        (type !== "daily" && "mix_chart" in data) && (
                          <>
                            <section className="page_contents">
                              <div className="content-block">
                                <div className="block-header">
                                  <div className="title">
                                    操作量と時間の推移
                                  </div>
                                </div>
                                <div className="block-detail">
                                  {
                                    data?.mix_chart ? (
                                      <MixChart
                                        isLoading={summary.isLoading}
                                        mixChart={data.mix_chart}
                                      />
                                    ) : (
                                      <>
                                        データがありません
                                      </>
                                    )
                                  }
                                </div>
                              </div>
                            </section>
                          </>
                        )
                      }
                      <Category
                        startDate={startDate || ""}
                        endDate={type === "daily" ? undefined : endDate}
                        reportData={data}
                        searchModalOpenCallback={
                          (search) => searchModalDetail({
                            startDate: search.startDate,
                            endDate: search.endDate,
                            teamIds: search.teamIds,
                            teamNames: search.teamNames,
                            memberIds: search.memberIds,
                            memberNames: search.memberNames,
                            keywords: search.keywords,
                            serviceNames: search.serviceNames,
                            categoryIds: search.categoryIds,
                            categoryNames: search.categoryNames,
                            efficiencyLevel: search.efficiencyLevel,
                            stressLevel: search.stressLevel,
                            focusLevel: search.focusLevel,
                          })
                        }
                      />
                      <Application
                        startDate={startDate || ""}
                        endDate={type === "daily" ? undefined : endDate}
                        reportData={data}
                        searchModalOpenCallback={
                          (search) => searchModalDetail({
                            startDate: search.startDate,
                            endDate: search.endDate,
                            teamIds: search.teamIds,
                            teamNames: search.teamNames,
                            memberIds: search.memberIds,
                            memberNames: search.memberNames,
                            keywords: search.keywords,
                            serviceNames: search.serviceNames,
                            categoryIds: search.categoryIds,
                            categoryNames: search.categoryNames,
                            efficiencyLevel: search.efficiencyLevel,
                            stressLevel: search.stressLevel,
                            focusLevel: search.focusLevel,
                          })
                        }
                      />
                      {
                        login.function_enabled_state?.task_management && (
                          <section className="page_contents">
                            <div className="content-block">
                              <div className="block-detail">
                                <div className="box summary">
                                  <MemberSummaryModule
                                    member={task.taskReportMember}
                                  />
                                </div>
                              </div>
                            </div>
                          </section>
                        )
                      }
                      <Wordcloud
                        startDate={startDate || nowDate()}
                        endDate={startDate || nowDate()}
                      />
                    </section>
                    <SearchModal
                      searchValue={searchModal}
                      isDisplay={isSearchModalDisplay}
                      closeCallback={() => setIsSearchModalDisplay(false)}
                    />
                  </>
                ) : (
                  <>
                    レポートがありません
                  </>
                )
              }
            </>
          )
        }
      </section>
    </section>
  )
}

export default Index
